import { type RefObject, useEffect, useMemo, useRef } from 'react';

const DELIMITER = ',';

type CsvData = string[][];

interface Options {
  mimeType?: string;
}

export const useTemporaryCsvObjectUrl = (data: CsvData, options: Options) => {
  const mimeType = options?.mimeType ?? 'text/csv;charset=utf-8';

  const objectUrlRef = useRef<string | null>(null);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      clearObjectUrlRef(objectUrlRef);
    };
  }, []);

  return useMemo(() => {
    clearObjectUrlRef(objectUrlRef);

    const escapeCell = createEscapeCell(DELIMITER);
    const csvContent = data.map((row) => row.map(escapeCell).join(DELIMITER)).join('\r\n');

    const blob = new Blob([csvContent], { type: mimeType });
    objectUrlRef.current = window.URL.createObjectURL(blob);
    return objectUrlRef.current;
  }, [data, mimeType]);
};

const createEscapeCell =
  (delimiter: string) =>
  (cell: string): string => {
    const needsQuotes =
      cell.includes(delimiter) || cell.includes('"') || cell.includes('\n') || cell.includes('\r');

    const escaped = cell.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

const clearObjectUrlRef = (objectUrlRef: RefObject<string | null>) => {
  if (!objectUrlRef.current) {
    return;
  }

  window.URL.revokeObjectURL(objectUrlRef.current);
  objectUrlRef.current = null;
};
